import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Button, IconPlus, IconTrash, Notice, Plate, SelectField, TextField, useToast } from "@wilco/ui";
import { useDeferredValue, useState, type SubmitEvent } from "react";
import { formatNumber, t } from "../i18n.ts";
import { stockApi } from "./api.ts";
import { parseQty } from "./qty.ts";
import { STOCK, suggestionsQuery } from "./queries.ts";
import { errorNotice, type NoticeState } from "./ShelfPage.tsx";
import type { ProductRef, Suggestion, WarehouseCode } from "./types.ts";
import { ACTIVITY_WAREHOUSES } from "./warehouses.ts";

const route = getRouteApi("/app/stock/requests/new");

interface DraftLine {
  product: ProductRef;
  qty: string;
}

const isoDate = (d: Date) => d.toISOString().slice(0, 10);

/**
 * Черновик заявки склада деятельности («Дозамовити», docs/plan.md, «Заявки
 * и дозаказ»). Подсказки считает сервер: расход в день за 90 дней, дни
 * запаса, срок поставки и страховые дни, кратность упаковки, минимум.
 * «Часто замовляєте» добавляет прошлое количество одним нажатием.
 */
export function RequestDraftPage() {
  const { warehouse = "SER" } = route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const hints = useQuery(suggestionsQuery(warehouse));
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [needDate, setNeedDate] = useState(() => isoDate(new Date(Date.now() + 2 * 86_400_000)));
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const create = useMutation({ mutationFn: stockApi.createRequest });

  const add = (product: ProductRef, qty: number) => {
    setLines((prev) => {
      const i = prev.findIndex((l) => l.product.sku === product.sku);
      if (i < 0) return [...prev, { product, qty: String(qty) }];
      return prev.map((l, j) => (j === i ? { ...l, qty: String(qty) } : l));
    });
  };

  const has = (sku: string) => lines.some((l) => l.product.sku === sku);

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    setNotice(null);
    const body = lines.map((l) => ({ sku: l.product.sku, qty: parseQty(l.qty) ?? 0 }));
    const bad = lines.find((l) => (parseQty(l.qty) ?? 0) <= 0);
    if (bad) {
      setNotice({ tone: "danger", title: t("draft.badQty", { name: bad.product.name }) });
      return;
    }
    create.mutate(
      { warehouse, needDate, lines: body },
      {
        onSuccess: (r) => {
          void queryClient.invalidateQueries({ queryKey: STOCK });
          toast({ title: t("draft.created", { number: r.number }), tone: "success" });
          void navigate({ to: "/stock/requests", search: { warehouse } });
        },
        onError: (err) => setNotice(errorNotice(err)),
      },
    );
  };

  const h = hints.data;
  return (
    <div className="page stock-wide">
      <h1 className="page-title">{t("draft.title")}</h1>
      <form className="draft" onSubmit={submit}>
        <Plate cut="lg" className="draft-head">
          <SelectField
            label={t("draft.warehouse")}
            value={warehouse}
            onChange={(e) => {
              setLines([]);
              void navigate({ to: "/stock/requests/new", search: { warehouse: e.target.value as WarehouseCode }, replace: true });
            }}
            options={ACTIVITY_WAREHOUSES.map((w) => ({ value: w.code, label: w.name }))}
          />
          <TextField label={t("draft.needDate")} type="date" value={needDate} min={isoDate(new Date())} onChange={(e) => setNeedDate(e.target.value)} />
        </Plate>

        <section className="stock-section" aria-labelledby="draft-lines">
          <h2 id="draft-lines" className="desk-group-title">
            {t("draft.lines")}
          </h2>
          {lines.length === 0 ? <p className="w-muted">{t("draft.linesEmpty")}</p> : null}
          <ul className="draft-lines">
            {lines.map((l, i) => (
              <li key={l.product.sku} className="draft-line">
                <span className="draft-line-name">
                  <span className="w-num">{l.product.sku}</span> {l.product.name}
                </span>
                <TextField
                  label={t("draft.qty")}
                  hideLabel
                  inputMode="decimal"
                  autoComplete="off"
                  value={l.qty}
                  onChange={(e) => setLines((prev) => prev.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))}
                  className="draft-qty"
                  aria-label={t("draft.qtyFor", { name: l.product.name })}
                />
                <span className="qty-uom">{l.product.uom}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t("draft.remove", { name: l.product.name })}
                  onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}
                >
                  <IconTrash />
                </Button>
              </li>
            ))}
          </ul>
          <ProductSearch onPick={(p) => add(p, 1)} />
        </section>

        {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}
        <Button type="submit" variant="primary" size="lg" block disabled={lines.length === 0 || create.isPending}>
          {t("draft.create")}
        </Button>
      </form>

      {h ? (
        <div className="draft-hints">
          <section className="stock-section" aria-labelledby="draft-suggest">
            <div className="section-head">
              <h2 id="draft-suggest" className="desk-group-title">
                {t("draft.suggestions")}
              </h2>
              {h.suggestions.length > 1 ? (
                <Button variant="secondary" onClick={() => h.suggestions.forEach((s) => add(s.product, s.qty))}>
                  {t("draft.addAll")}
                </Button>
              ) : null}
            </div>
            {h.suggestions.length === 0 ? <p className="w-muted">{t("draft.noSuggestions")}</p> : null}
            <ul className="hint-list">
              {h.suggestions.map((s) => (
                <li key={s.product.sku} className="hint">
                  <span className="hint-name">{s.product.name}</span>
                  <span className="hint-why w-muted">{why(s)}</span>
                  <Button variant="secondary" className="hint-add" disabled={has(s.product.sku)} onClick={() => add(s.product, s.qty)}>
                    {has(s.product.sku) ? t("draft.added") : `+\u00a0${formatNumber(s.qty)}\u00a0${s.product.uom}`}
                  </Button>
                </li>
              ))}
            </ul>
          </section>
          <section className="stock-section" aria-labelledby="draft-frequent">
            <h2 id="draft-frequent" className="desk-group-title">
              {t("draft.frequent")}
            </h2>
            <ul className="hint-list">
              {h.frequent.map((f) => (
                <li key={f.product.sku} className="hint">
                  <span className="hint-name">{f.product.name}</span>
                  <span className="hint-why w-muted">{t("draft.frequentWhy", { times: f.times, qty: formatNumber(f.lastQty), uom: f.product.uom })}</span>
                  <Button variant="secondary" className="hint-add" disabled={has(f.product.sku)} onClick={() => add(f.product, f.lastQty)}>
                    {has(f.product.sku) ? t("draft.added") : `+\u00a0${formatNumber(f.lastQty)}\u00a0${f.product.uom}`}
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        </div>
      ) : null}
    </div>
  );
}

/** Почему предложено: дни запаса против срока поставки или минимум. */
function why(s: Suggestion): string {
  const parts = [
    s.dailyUse === null ? t("draft.noUse") : t("draft.use", { qty: formatNumber(s.dailyUse), uom: s.product.uom }),
    // Срок поставки и страховые дни сервер в совете не отдаёт: тогда только дни запаса.
    s.daysOfStock === null
      ? null
      : s.leadDays + s.safetyDays > 0
        ? t("draft.days", { days: formatNumber(s.daysOfStock), lead: s.leadDays + s.safetyDays })
        : t("draft.daysOnly", { days: formatNumber(s.daysOfStock) }),
    s.reason === "below_min" && s.min !== null ? t("draft.belowMin", { min: formatNumber(s.min), uom: s.product.uom }) : null,
  ];
  return parts.filter(Boolean).join(" · ");
}

/** Поиск товара справочника по артикулу и названию: заявка, заказ покупателя, ремонт. */
export function ProductSearch({ onPick }: { onPick: (p: ProductRef) => void }) {
  const [q, setQ] = useState("");
  const query = useDeferredValue(q.trim());
  const found = useQuery({ queryKey: [...STOCK, "products", query], queryFn: () => stockApi.products(query), enabled: query.length >= 2 });
  return (
    <div className="product-search">
      <TextField label={t("draft.search")} placeholder={t("draft.searchHint")} value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" />
      {query.length >= 2 && found.data ? (
        <ul className="product-search-list">
          {found.data.length === 0 ? <li className="w-muted">{t("palette.empty")}</li> : null}
          {found.data.map((p) => (
            <li key={p.sku}>
              <button
                type="button"
                className="product-search-item"
                onClick={() => {
                  onPick(p);
                  setQ("");
                }}
              >
                <IconPlus />
                <span className="w-num">{p.sku}</span> {p.name}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
